import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Minus, Plus } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { api, imgUrl } from "@/lib/api";
import { BrandBadge, StockBadge } from "@/components/shop/Badges";
import { OptionsPicker } from "@/components/shop/OptionsPicker";
import { InquiryDialog } from "@/components/shop/InquiryDialog";
import { InquirySent } from "@/components/shop/InquirySent";
import { NetPrice } from "@/components/shop/Totals";
import { SaleBadge, GiftBox } from "@/components/shop/Badges";
import { CompareToggle } from "@/components/shop/Compare";

const Gallery = ({ images, name }) => {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div className="aspect-square img-stage border border-[#3B4157] overflow-hidden">
        {images[active] && <img data-testid="product-main-image" src={imgUrl(images[active])} alt={name} className="w-full h-full object-contain p-8" />}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((im, i) => (
            <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setActive(i)}
              className={`w-20 h-20 shrink-0 border overflow-hidden img-stage transition-colors ${i === active ? "border-orange-500" : "border-[#3B4157] opacity-60 hover:opacity-100"}`}>
              <img src={imgUrl(im)} alt="" className="w-full h-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState([]);
  const [ask, setAsk] = useState(null);
  const [sent, setSent] = useState(null);
  const tracked = useRef(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
    if (tracked.current !== id) {
      tracked.current = id;
      api.post(`/track/view/${id}`, { path: `/product/${id}`, referrer: document.referrer }).catch(() => {});
    }
  }, [id]);

  useSeo(p ? {
    title: `${p.name} – ${p.name.includes(p.brand) ? "" : p.brand + " "}${t("seo_product")}`,
    description: ((lang !== "lt" ? p.description_en || p.description_lt : p.description_lt || p.description_en) || "").replace(/\s+/g, " ").slice(0, 160),
    image: p.images?.[0] ? imgUrl(p.images[0]) : undefined,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.sku || undefined,
      brand: { "@type": "Brand", name: p.brand }, image: (p.images || []).slice(0, 5).map(imgUrl),
      description: (p.description_lt || "").slice(0, 500),
      offers: { "@type": "Offer", priceCurrency: "EUR", price: (p.price * 1.21).toFixed(2), url: window.location.href,
        availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/BackOrder", itemCondition: "https://schema.org/NewCondition" },
    },
  } : {});

  if (p === null) return <div className="h-96" />;
  if (p === false) return <div className="text-center py-32 text-slate-400">404</div>;

  if (sent) return <InquirySent number={sent.number} />;
  const opts = (p.options || []).filter((o) => o.enabled);
  const chosen = opts.filter((o) => sel.includes(o.key));
  const desc = lang !== "lt" ? p.description_en || p.description_lt : p.description_lt || p.description_en;

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <Link to="/catalog" data-testid="product-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-orange-500">
        <ArrowLeft className="w-4 h-4" /> {t("back")}
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-6 [&>*]:min-w-0">
        <Gallery images={p.images || []} name={p.name} />
        <div className="rise">
          <div className="flex items-center gap-3">
            <BrandBadge brand={p.brand} />
            <span className="text-[10px] font-mono-x uppercase tracking-wider text-slate-500">{t("sku")}: {p.sku || "—"}</span>
            <CompareToggle id={p.id} className="ml-auto" />
          </div>
          <h1 data-testid="product-name" className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-4 leading-none">{p.name}</h1>
          <div className="text-xs font-mono-x uppercase tracking-wider text-slate-500 mt-3">{t(p.machine_type)} · {t(p.suspension)}</div>
          {p.fits && <div data-testid="product-fits" className="mt-4 text-sm"><span className="text-slate-500">{t("fits_label")}:</span> <span className="text-orange-400 font-semibold">{p.fits}</span></div>}
          {p.universal && <div data-testid="product-fits-all" className="mt-4 text-xs font-mono-x uppercase tracking-wider text-emerald-400">{t("fits_all")}</div>}
          {p.model && !p.fits && <div className="mt-4 text-xs font-mono-x uppercase tracking-wider text-emerald-400">{t("universal_model")}</div>}
          {!p.fits && p.compatible?.length > 0 && <div data-testid="product-compatible" className="mt-3 text-sm"><span className="text-slate-500">{t("fits_tractors")}:</span> <span className="inline-flex flex-wrap gap-1.5 mt-1.5 align-middle">{[...p.compatible].sort().map((c) => <span key={c} className="px-2 py-0.5 border border-[#3B4157] bg-[#292D3C] text-xs text-slate-200">{c}</span>)}</span></div>}
          {p.price_on_request ? (
            <div className="mt-8">
              <div data-testid="product-price" className="font-display font-black text-4xl text-orange-500">{t("ask_price")}</div>
              <p className="text-xs text-slate-400 mt-2">{p.fits && `${t("variant_includes")}. `}{t("ask_price_note")}</p>
              <button data-testid="product-ask-price-btn" onClick={() => setAsk({ product: p, options: [] })}
                className="w-full h-12 mt-6 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors">{t("ask_price")}</button>
            </div>
          ) : (<>
          <div className="mt-8 flex items-end gap-3"><NetPrice value={p.price} old={p.old_price} testid="product-price" className="font-display font-black text-5xl text-orange-500" />
            {p.old_price > p.price && <SaleBadge pct={p.discount_pct} testid="product-sale-badge" />}</div>
          <div className="mt-3"><StockBadge stock={p.stock} testid="product-stock" /></div>
          <GiftBox p={p} />
          {opts.length > 0 && <OptionsPicker options={opts} selected={sel} setSelected={setSel} />}
          {chosen.length > 0 ? (
            <div className="mt-6">
              <button data-testid="product-send-inquiry-btn" onClick={() => setAsk({ product: p, options: chosen })}
                className="w-full h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors">{t("send_inquiry")}</button>
              <p className="text-xs text-amber-400 mt-2">{t("inquiry_note")}</p>
            </div>
          ) : (
          <div className="flex gap-3 mt-8">
            <div className="flex items-center border border-[#3B4157]">
              <button data-testid="product-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 grid place-items-center hover:text-orange-500"><Minus className="w-4 h-4" /></button>
              <span data-testid="product-qty" className="w-10 text-center font-mono-x">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty(Math.min(50, qty + 1))} className="w-11 h-12 grid place-items-center hover:text-orange-500"><Plus className="w-4 h-4" /></button>
            </div>
            <button data-testid="product-add-to-cart-btn" onClick={() => add(p, qty)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:bg-slate-700 disabled:text-slate-500 transition-colors">
              <ShoppingCart className="w-4 h-4" /> {t("add_cart")}
            </button>
          </div>
          )}
          </>)}
          <Link to="/seat-finder" data-testid="product-finder-link" className="block mt-4 text-sm text-slate-400 hover:text-orange-500">{t("finder_link")} →</Link>
          {desc && (
            <div className="mt-12">
              <h2 className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500">{t("description")}</h2>
              <p data-testid="product-description" className="text-slate-300 mt-3 leading-relaxed whitespace-pre-line">{desc}</p>
            </div>
          )}
          {p.specs?.length > 0 && (
            <div className="mt-10">
              <h2 className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500">{t("specs")}</h2>
              <dl data-testid="product-specs" className="mt-3 border border-[#3B4157] divide-y divide-[#3B4157]">
                {p.specs.map((s, i) => (
                  <div key={i} className="grid grid-cols-2 px-4 py-3 text-sm">
                    <dt className="text-slate-400">{lang !== "lt" ? s.key_en || s.key_lt : s.key_lt || s.key_en}</dt>
                    <dd className="font-mono-x text-right">{lang !== "lt" ? s.value_en || s.value : s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
      {ask && <InquiryDialog product={ask.product} options={ask.options} total={ask.product.price + ask.options.reduce((s, o) => s + o.price, 0)}
        onClose={() => setAsk(null)} onDone={(d) => { setAsk(null); setSent(d); window.scrollTo(0, 0); }} />}
    </div>
  );
}
